import { Injectable } from '@angular/core';
import { Octokit } from '@octokit/rest';
import { retry } from '@octokit/plugin-retry';
import { Observable, from, of, throwError, forkJoin, BehaviorSubject } from 'rxjs';
import { catchError, map, tap, switchMap, filter, distinctUntilKeyChanged } from 'rxjs/operators';
import { GitHubUser, GitHubOrganization, GitHubRepository, GitHubBranch, GitHubRef } from '../models/github.models';
import { GitHubAuthService } from './github-auth.service';
import { CacheService } from './cache.service';

@Injectable({
  providedIn: 'root'
})
export class GitHubApiService {
  private octokit: Octokit | null = null;
  private readonly MyOctokit = Octokit.plugin(retry);

  // Separate observables for user data - backed by cache
  private readonly userData = new BehaviorSubject<GitHubUser | null>(null);
  private readonly organizations = new BehaviorSubject<GitHubOrganization[]>([]);
  private readonly repositories = new BehaviorSubject<GitHubRepository[]>([]);

  // Public observables for components to subscribe to
  public user$ = this.userData.asObservable();
  public organizations$ = this.organizations.asObservable();
  public repositories$ = this.repositories.asObservable();

  private readonly CACHE_KEYS = {
    USER_DATA: 'github_user_data',
    USER_PROFILE: 'github_user_profile',
    USER_ORGS: 'github_user_orgs',
    USER_REPOS: 'github_user_repos'
  };
  private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

  constructor(
    private readonly githubAuth: GitHubAuthService,
    private readonly cacheService: CacheService
  ) {
    // Subscribe to connection info changes (not the full state)
    this.githubAuth.connectionInfo$
      .pipe(
        distinctUntilKeyChanged('isConnected'),
        filter(info => info.isConnected && !!this.githubAuth.getAccessToken())
      )
      .subscribe(() => {
        console.log('[GitHubApiService] Connection established, initializing');
        this.initializeOctokit();
        this.loadUserData();
      });
  }

  private initializeOctokit(): void {
    if(this.octokit !== null) {
      // Already initialized
      return;
    }
    const accessToken = this.githubAuth.getAccessToken();
    if (!accessToken) {
      console.warn('[GitHubApiService] No access token available to initialize Octokit');
      return;
    }

    this.octokit ??= new this.MyOctokit({
      auth: accessToken,
      baseUrl: 'https://api.github.com',
      userAgent: 'Platform Portal v1.0.0',
      retry: {
        doNotRetry: [401] // Retry all errors except 401
      }
    });

    // Add error hook for 401 handling with RxJS
    this.octokit.hook.error('request', (error: any, options: any) => {
      if (error.status === 401 && this.octokit !== null) {
        if(this.octokit === null) return;
        this.octokit = null;
        this.githubAuth.logout(); // Clear current auth state
         // Clear Octokit instance
        console.warn('[GitHubApiService] Caught 401 Unauthorized');



      }
      throw error;
    });
  }



  /**
   * Gets the current authenticated user's information
   */
  public getCurrentUser(): Observable<GitHubUser> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.users.getAuthenticated()).pipe(
      map(response => response.data as GitHubUser),
      catchError(error => {
        console.error('Error fetching GitHub user:', error);
        return throwError(() => new Error('Failed to fetch user information'));
      })
    );
  }

  /**
   * Gets the organizations the user belongs to
   */
  public getUserOrganizations(): Observable<GitHubOrganization[]> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.orgs.listForAuthenticatedUser()).pipe(
      map(response => response.data as GitHubOrganization[]),
      catchError(error => {
        console.error('Error fetching GitHub organizations:', error);
        return throwError(() => new Error('Failed to fetch organizations'));
      })
    );
  }

  /**
   * Gets the repositories the user has access to
   */
  public getUserRepositories(options: {
    visibility?: 'all' | 'public' | 'private';
    affiliation?: 'owner' | 'collaborator' | 'organization_member';
    sort?: 'created' | 'updated' | 'pushed' | 'full_name';
    direction?: 'asc' | 'desc';
    per_page?: number;
  } = {}): Observable<GitHubRepository[]> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    const defaultOptions = {
      visibility: 'all' as const,
      affiliation: 'owner,collaborator,organization_member',
      sort: 'updated' as const,
      direction: 'desc' as const,
      per_page: 100
    };

    const params = { ...defaultOptions, ...options };

    return from(this.octokit.rest.repos.listForAuthenticatedUser(params)).pipe(
      map(response => response.data as GitHubRepository[]),
      catchError(error => {
        console.error('Error fetching GitHub repositories:', error);
        return throwError(() => new Error('Failed to fetch repositories'));
      })
    );
  }

  /**
   * Gets all repositories for a given organization, handling pagination.
   */
  public getOrganizationRepositories(org: string): Observable<GitHubRepository[]> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.paginate(this.octokit.rest.repos.listForOrg, {
      org: org,
      type: 'all',
      per_page: 100
    })).pipe(
      map(response => response as GitHubRepository[]),
      catchError(error => {
        console.error(`Error fetching repositories for organization ${org}:`, error);
        return throwError(() => new Error('Failed to fetch organization repositories'));
      })
    );
  }

  /**
   * Creates a new repository in an organization.
   */
  public createRepository(options: {
    org: string,
    name: string;
    description?: string;
    private?: boolean;
    auto_init?: boolean;
    gitignore_template?: string;
    license_template?: string;
  }): Observable<GitHubRepository> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.repos.createInOrg(options)).pipe(
      map(response => response.data as GitHubRepository),
      catchError(error => {
        console.error('Error creating GitHub repository:', error);
        return throwError(() => new Error('Failed to create repository'));
      })
    );
  }

  /**
   * Gets repository information
   */
  public getRepository(owner: string, repo: string): Observable<GitHubRepository> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.repos.get({
      owner,
      repo
    })).pipe(
      map(response => response.data as GitHubRepository),
      catchError(error => {
        console.error('Error fetching GitHub repository:', error);
        return throwError(() => new Error('Failed to fetch repository'));
      })
    );
  }

  /**
   * Gets a branch from a repository
   */
  public getBranch(owner: string, repo: string, branch: string): Observable<GitHubBranch> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.repos.getBranch({
      owner,
      repo,
      branch
    })).pipe(
      map(response => response.data as GitHubBranch),
      catchError(error => {
        console.error('Error fetching GitHub branch:', error);
        return throwError(() => new Error('Failed to fetch branch'));
      })
    );
  }

  /**
   * Gets all branches from a repository, handling pagination.
   */
  public getBranches(owner: string, repo: string): Observable<GitHubBranch[]> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.paginate(this.octokit.rest.repos.listBranches, {
      owner,
      repo,
      per_page: 100
    })).pipe(
      map(response => response as GitHubBranch[]),
      catchError(error => {
        console.error(`Error fetching branches for repository ${owner}/${repo}:`, error);
        return throwError(() => new Error('Failed to fetch branches'));
      })
    );
  }

  /**
   * Creates a new repository from a template
   */
  public createRepositoryFromTemplate(templateOwner: string, templateRepo: string, options: {
    owner: string,
    name: string,
    description?: string,
    private?: boolean,
  }): Observable<GitHubRepository> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.repos.createUsingTemplate({
      template_owner: templateOwner,
      template_repo: templateRepo,
      ...options
    })).pipe(
      map(response => response.data as GitHubRepository),
      catchError(error => {
        console.error('Error creating GitHub repository from template:', error);
        return throwError(() => new Error('Failed to create repository from template'));
      })
    );
  }

  /**
   * Updates a repository
   */
  public updateRepository(owner: string, repo: string, options: any): Observable<GitHubRepository> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.repos.update({
      owner,
      repo,
      ...options
    })).pipe(
      map(response => response.data as GitHubRepository),
      catchError(error => {
        console.error('Error updating GitHub repository:', error);
        return throwError(() => new Error('Failed to update repository'));
      })
    );
  }

  /**
   * Creates a new branch in a repository
   */
  public createBranch(owner: string, repo: string, branchName: string, fromSha: string): Observable<GitHubRef> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.git.createRef({
      owner,
      repo,
      ref: `refs/heads/${branchName}`,
      sha: fromSha
    })).pipe(
      map(response => response.data as GitHubRef),
      catchError(error => {
        console.error('Error creating GitHub branch:', error);

        // Handle the specific case of "Reference already exists"
        if (error.status === 422 && error.message?.includes('Reference already exists')) {
          const branchUrl = `https://github.com/${owner}/${repo}/tree/${branchName}`;
          const specificError = new Error(`Branch '${branchName}' already exists`);
          (specificError as any).branchUrl = branchUrl;
          (specificError as any).branchExists = true;
          return throwError(() => specificError);
        }

        return throwError(() => new Error('Failed to create branch'));
      })
    );
  }

  /**
   * Creates a new pull request
   */
  public createPullRequest(options: {
    owner: string;
    repo: string;
    title: string;
    head: string;
    base: string;
    body?: string;
    draft?: boolean;
  }): Observable<any> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.pulls.create(options)).pipe(
      map(response => response.data),
      catchError(error => {
        console.error('Error creating GitHub pull request:', error);
        return throwError(() => new Error('Failed to create pull request'));
      })
    );
  }

  /**
   * Creates a new issue
   */
  public createIssue(options: {
    owner: string;
    repo: string;
    title: string;
    body?: string;
    assignees?: string[];
    labels?: string[];
  }): Observable<any> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.issues.create(options)).pipe(
      map(response => response.data),
      catchError(error => {
        console.error('Error creating GitHub issue:', error);
        return throwError(() => new Error('Failed to create issue'));
      })
    );
  }

  /**
   * Gets repository content
   */
  public getRepositoryContent(owner: string, repo: string, path: string = ''): Observable<any> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.repos.getContent({
      owner,
      repo,
      path
    })).pipe(
      map(response => response.data),
      catchError(error => {
        console.error('Error fetching repository content:', error);
        return throwError(() => new Error('Failed to fetch repository content'));
      })
    );
  }

  /**
   * Gets all files in a repository recursively.
   */
  public getRepositoryFiles(owner: string, repo: string): Observable<any[]> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    return from(this.octokit.rest.repos.get({ owner, repo })).pipe(
      switchMap((repoData: any) => {
        const defaultBranch = repoData.data.default_branch;
        return from(this.octokit!.rest.git.getTree({
          owner,
          repo,
          tree_sha: defaultBranch,
          recursive: 'true'
        }));
      }),
      map((response: any) => response.data.tree.filter((node: any) => node.type === 'blob')),
      catchError(error => {
        console.error('Error fetching repository files recursively:', error);
        return throwError(() => new Error('Failed to fetch repository files'));
      })
    );
  }

  /**
   * Gets the content of a specific file in a repository as text.
   */
  public getRepositoryFileAsText(owner: string, repo: string, path: string): Observable<string> {
    return this.getRepositoryContent(owner, repo, path).pipe(
      map(data => {
        if (data.encoding !== 'base64') {
          throw new Error('Unsupported file encoding');
        }
        // Decode base64 content
        return atob(data.content);
      }),
      catchError(error => {
        console.error(`Error fetching file content for ${path}:`, error);
        return throwError(() => new Error(`Failed to fetch file content for ${path}`));
      })
    );
  }

  /**
   * Creates or updates a file in a repository
   */
  public updateFileContent(options: {
    owner: string;
    repo: string;
    path: string;
    message: string;
    content: string;
    branch?: string;
    sha?: string;
  }): Observable<any> {
    if (!this.octokit) {
      return throwError(() => new Error('GitHub not connected'));
    }

    // Encode content to base64
    const encodedContent = btoa(options.content);

    return from(this.octokit.rest.repos.createOrUpdateFileContents({
      owner: options.owner,
      repo: options.repo,
      path: options.path,
      message: options.message,
      content: encodedContent,
      branch: options.branch,
      sha: options.sha
    })).pipe(
      map(response => response.data),
      catchError(error => {
        console.error('Error updating file content:', error);
        return throwError(() => new Error('Failed to update file content'));
      })
    );
  }

  /**
   * Loads user data from cache or fetches fresh data
   * Called automatically when connection is established
   */
  private loadUserData(): void {
    this.refreshUserData(false).subscribe({
      next: () => {
        console.log('[GitHubApiService] User data loaded successfully');
      },
      error: (error) => {
        console.error('[GitHubApiService] Failed to load user data:', error);
      }
    });
  }

  /**
   * Refreshes user data and updates observables
   * @param forceRefresh If true, bypasses cache and fetches fresh data
   */
  public refreshUserData(forceRefresh: boolean = false): Observable<void> {
    if (!this.githubAuth.isConnected()) {
      return throwError(() => new Error('Not connected to GitHub'));
    }

    // Create cache key that includes the current user's access token for cache separation
    const accessToken = this.githubAuth.getAccessToken();
    const hashedToken = this.hashToken(accessToken || 'no-token');
    const cacheKey = this.cacheService.generateCacheKey(
      this.CACHE_KEYS.USER_DATA,
      [hashedToken]
    );

    // If forceRefresh is true, remove the cache first
    if (forceRefresh) {
      this.cacheService.remove(cacheKey);
    }

    // Define the fetch function that contains the existing forkJoin logic
    const fetchUserData = () => {
      return forkJoin({
        user: this.getCurrentUser(),
        organizations: this.getUserOrganizations(),
        repositories: this.getUserRepositories({ per_page: 50 })
      });
    };

    // Use cache service to get cached data or fetch fresh data
    return this.cacheService.getCachedOrFetch(
      cacheKey,
      this.CACHE_DURATION,
      fetchUserData
    ).pipe(
      tap(data => {
        // Add null check before destructuring to prevent errors when cache returns null
        if (data && typeof data === 'object') {
          const { user, organizations, repositories } = data;

          // Update the separate BehaviorSubjects
          this.userData.next(user || null);
          this.organizations.next(organizations || []);
          this.repositories.next(repositories || []);

          // Update only the connection info (not the full state with data)
          this.githubAuth.updateConnectionInfo({
            lastSync: new Date(),
            error: null
          });
        } else {
          // Log when data is null/undefined (initial cache miss before fetch completes)
          console.log('[GitHubApiService] Waiting for user data to load...');
        }
      }),
      map(() => void 0),
      catchError(error => {
        console.error('Error refreshing GitHub user data:', error);
        this.githubAuth.updateConnectionInfo({
          error: 'Failed to refresh user data'
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Hashes a token using SHA-256 for secure cache key generation
   */
  private hashToken(token: string): string {
    // Use a simple but effective hash for cache key generation
    // This provides reasonable security while being synchronous and browser-compatible
    let hash = 0;
    if (token.length === 0) return 'empty';

    for (let i = 0; i < token.length; i++) {
      const char = token.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    // Convert to a positive hex string for consistent cache key format
    return Math.abs(hash).toString(16);
  }

  /**
   * Tests the GitHub connection
   */
  public testConnection(): Observable<boolean> {
    if (!this.octokit) {
      return of(false);
    }

    return this.getCurrentUser().pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }
}
